import { useId, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  Tabs,
  useToast,
} from '@/components/ui';
import {
  useConsentTemplateHistory,
  useCreateConsentTemplate,
  useUpdateConsentTemplate,
} from '@/features/consent-templates/api';
import {
  CONSENT_PROCEDURES,
  CONSENT_TEMPLATE_LIMITS,
  WORDING_SECTIONS,
  type ConsentTemplate,
  type ConsentWording,
  type WordingSection,
} from '@/features/consent-templates/types';
import { useServices } from '@/features/queue/api';
import { isApiError } from '@/lib/api';
import { FailureBox } from './ConsentFailureBox';
import { ConsentWordingPreview } from './ConsentWordingPreview';
import {
  LANG_LABEL,
  RELOAD_CODES,
  SECTION_HEADING,
  toFailure,
  type Failure,
  type WordingLang,
} from './consent-format';
import styles from './ConsentTemplates.module.css';

export type FormMode =
  | { kind: 'create' }
  | { kind: 'revise'; base: ConsentTemplate }
  | { kind: 'edit'; template: ConsentTemplate };

interface Values extends ConsentWording {
  key: string;
  procedureCode: string;
  serviceId: string;
}

type Errors = Partial<Record<string, string>>;

const LANGS: WordingLang[] = ['en', 'ms'];
const blank = { en: '', ms: '' };

function valuesOf(t?: ConsentTemplate): Values {
  return {
    key: t?.key ?? '',
    procedureCode: t?.procedureCode ?? '',
    serviceId: t?.service?.id ?? '',
    title: { ...(t?.title ?? blank) },
    whatHappens: { ...(t?.whatHappens ?? blank) },
    risks: { ...(t?.risks ?? blank) },
    alternatives: { ...(t?.alternatives ?? blank) },
  };
}

function validate(v: Values, withKey: boolean): Errors {
  const e: Errors = {};
  if (withKey && !CONSENT_TEMPLATE_LIMITS.keyPattern.test(v.key))
    e.key =
      'Use 2–60 lower-case letters, digits or “_”, starting with a letter.';
  if (!v.procedureCode) e.procedureCode = 'Choose the procedure.';
  for (const lang of LANGS) {
    if (!v.title[lang].trim())
      e[`title.${lang}`] = `Enter the title in ${LANG_LABEL[lang]}.`;
    for (const s of WORDING_SECTIONS)
      if (!v[s][lang].trim())
        e[`${s}.${lang}`] =
          `Enter “${SECTION_HEADING.en[s]}” in ${LANG_LABEL[lang]}.`;
  }
  return e;
}

const trimmed = (v: Values): ConsentWording => ({
  title: { en: v.title.en.trim(), ms: v.title.ms.trim() },
  whatHappens: { en: v.whatHappens.en.trim(), ms: v.whatHappens.ms.trim() },
  risks: { en: v.risks.en.trim(), ms: v.risks.ms.trim() },
  alternatives: { en: v.alternatives.en.trim(), ms: v.alternatives.ms.trim() },
});

/**
 * Write a consent template draft (RL-1136): a new key (revision 1), the next
 * revision of a key (prefilled from the revision it starts from) or an edit
 * of a draft (sends the draft's `version`). Every text is required in English
 * and Bahasa Melayu; "Preview" shows it as the patient reads it.
 */
export function ConsentTemplateForm({
  mode,
  onClose,
  onSaved,
}: {
  mode: FormMode;
  onClose: () => void;
  onSaved: (saved: ConsentTemplate) => void;
}) {
  const formId = useId();
  const toast = useToast();
  const create = useCreateConsentTemplate();
  const update = useUpdateConsentTemplate();
  const services = useServices();
  const source =
    mode.kind === 'edit'
      ? mode.template
      : mode.kind === 'revise'
        ? mode.base
        : undefined;
  // An edit keeps sending the latest `version` (reloaded after a conflict).
  const history = useConsentTemplateHistory(
    source?.key ?? '',
    mode.kind === 'edit',
  );
  const current =
    mode.kind === 'edit'
      ? (history.data?.find((t) => t.id === mode.template.id) ?? mode.template)
      : undefined;

  const [values, setValues] = useState<Values>(() => valuesOf(source));
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const busy = create.isPending || update.isPending;
  const withKey = mode.kind === 'create';

  const title =
    mode.kind === 'create'
      ? 'New consent template'
      : mode.kind === 'revise'
        ? `New revision of ${mode.base.title.en}`
        : `Edit draft revision ${mode.template.revision}`;

  function set<K extends keyof Values>(k: K, value: Values[K]) {
    setValues((v) => ({ ...v, [k]: value }));
  }
  function setText(
    k: 'title' | WordingSection,
    lang: WordingLang,
    text: string,
  ) {
    setValues((v) => ({ ...v, [k]: { ...v[k], [lang]: text } }));
    setErrors((e) => ({ ...e, [`${k}.${lang}`]: undefined }));
  }

  async function save() {
    setFailure(null);
    const found = validate(values, withKey);
    setErrors(found);
    if (Object.keys(found).length) {
      setTab('write');
      return;
    }
    const wording = trimmed(values);
    try {
      const saved =
        mode.kind === 'edit'
          ? await update.mutateAsync({
              id: mode.template.id,
              body: {
                version: current!.version,
                procedureCode: values.procedureCode,
                serviceId: values.serviceId || null,
                ...wording,
              },
            })
          : await create.mutateAsync({
              key: withKey ? values.key : source!.key,
              procedureCode: values.procedureCode,
              serviceId: values.serviceId || null,
              ...wording,
            });
      toast.show({
        tone: 'success',
        title:
          mode.kind === 'edit'
            ? `Draft revision ${saved.revision} saved`
            : `Draft revision ${saved.revision} created: a doctor approves it before patients can sign it`,
      });
      onSaved(saved);
    } catch (err) {
      if (isApiError(err) && err.fieldErrors.length) {
        setErrors(
          Object.fromEntries(err.fieldErrors.map((f) => [f.field, f.message])),
        );
        setTab('write');
      }
      setFailure(toFailure(err));
      if (isApiError(err) && RELOAD_CODES.has(err.code)) void history.refetch();
    }
  }

  const serviceOptions = [
    ...(services.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    // Keep the linked service selectable even when it is no longer listed.
    ...(source?.service &&
    !services.data?.some((s) => s.id === source.service!.id)
      ? [{ value: source.service.id, label: source.service.name }]
      : []),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      wide
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={busy}>
            {mode.kind === 'edit' ? 'Save draft' : 'Create draft'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {failure && <FailureBox failure={failure} />}
        {mode.kind === 'edit' &&
          current &&
          current.version !== mode.template.version && (
            <p className={styles.note} role="status">
              This draft was changed by someone else since you opened it. Your
              text is kept; saving replaces theirs.{' '}
              <Button
                size="sm"
                onClick={() => {
                  setValues(valuesOf(current));
                  setErrors({});
                  setFailure(null);
                }}
              >
                Load their latest text
              </Button>
            </p>
          )}
        {mode.kind === 'revise' && (
          <p className={styles.note}>
            Starts from revision {mode.base.revision}. The new revision stays a
            draft until a doctor approves it; revision {mode.base.revision}{' '}
            stays as it is until then.
          </p>
        )}
        <Tabs
          label="Draft view"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'write', label: 'Write' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
        {tab === 'preview' ? (
          <ConsentWordingPreview wording={values} />
        ) : (
          <>
            <div className={styles.pair}>
              {withKey ? (
                <Field
                  label="Template key"
                  hint="A stable name shared by every revision, e.g. skin_tag_removal. It cannot change later."
                  error={errors.key}
                >
                  <Input
                    value={values.key}
                    maxLength={60}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) => {
                      set('key', e.target.value);
                      setErrors((er) => ({ ...er, key: undefined }));
                    }}
                  />
                </Field>
              ) : (
                <Field label="Template key">
                  <Input value={source!.key} readOnly />
                </Field>
              )}
              <Field
                label="Procedure"
                hint="Signing this consent satisfies this procedure in the visit."
                error={errors.procedureCode}
              >
                <Select
                  value={values.procedureCode}
                  placeholder="Choose a procedure"
                  options={CONSENT_PROCEDURES.map((p) => ({
                    value: p.code,
                    label: p.name,
                  }))}
                  onChange={(e) => {
                    set('procedureCode', e.target.value);
                    setErrors((er) => ({ ...er, procedureCode: undefined }));
                  }}
                />
              </Field>
              <Field
                label="Service"
                hint={
                  services.isError
                    ? 'Could not load the services.'
                    : 'Bookings of this service need this consent. None = only bookings the desk points at it.'
                }
                error={errors.serviceId}
              >
                <Select
                  value={values.serviceId}
                  placeholder="No service"
                  options={serviceOptions}
                  disabled={services.isLoading}
                  onChange={(e) => set('serviceId', e.target.value)}
                />
              </Field>
            </div>
            <fieldset className={styles.fieldset}>
              <legend className={styles.legend}>Title</legend>
              <div className={styles.pair}>
                {LANGS.map((lang) => (
                  <Field
                    key={lang}
                    label={`Title (${LANG_LABEL[lang]})`}
                    error={errors[`title.${lang}`]}
                  >
                    <Input
                      lang={lang}
                      value={values.title[lang]}
                      maxLength={CONSENT_TEMPLATE_LIMITS.titleMax}
                      onChange={(e) => setText('title', lang, e.target.value)}
                    />
                  </Field>
                ))}
              </div>
            </fieldset>
            {WORDING_SECTIONS.map((s) => (
              <fieldset key={s} className={styles.fieldset}>
                <legend className={styles.legend}>
                  {SECTION_HEADING.en[s]}
                </legend>
                <div className={styles.pair}>
                  {LANGS.map((lang) => (
                    <Field
                      key={lang}
                      label={`${SECTION_HEADING.en[s]} (${LANG_LABEL[lang]})`}
                      error={errors[`${s}.${lang}`]}
                    >
                      <textarea
                        lang={lang}
                        className={styles.textarea}
                        value={values[s][lang]}
                        maxLength={CONSENT_TEMPLATE_LIMITS.textMax}
                        onChange={(e) => setText(s, lang, e.target.value)}
                      />
                    </Field>
                  ))}
                </div>
              </fieldset>
            ))}
          </>
        )}
      </form>
    </Dialog>
  );
}
